import { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import * as api from "@/lib/api";
import { ShieldCheck, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import BackupPanel from "@/components/lab/BackupPanel";

const EVENTS = { otp_request: "Richiesta codice", otp_verify: "Verifica codice", login: "Accesso", logout: "Uscita", private_unlock: "Apertura area privata", private_lock: "Chiusura area privata" };
const REASONS = {
  otp_sent: "Codice inviato su Telegram", unknown_identifier: "Credenziali non riconosciute", rate_limit: "Limite richieste superato",
  telegram_send_failed: "Invio Telegram non riuscito", ip_locked: "Verifica bloccata (troppi errori)", invalid_or_used: "Codice non valido o già usato",
  expired: "Codice scaduto", too_many_attempts: "Troppi tentativi", wrong_code: "Codice errato", wrong_code_locked: "Codice errato · bloccato",
  otp_verified: "OTP verificato", wrong_password: "Password errata", private_opened: "Area privata aperta", private_closed: "Area privata chiusa",
};

const device = (ua) => {
  const os = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac OS/.test(ua) ? "macOS" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "";
  const br = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : "";
  return [br, os].filter(Boolean).join(" · ") || "Dispositivo sconosciuto";
};

export default function SicurezzaDrawer({ open, setOpen, onRestored }) {
  const [view, setView] = useState("logs");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.authActivity().then(setLogs).catch((e) => console.error("Attività:", e)).finally(() => setLoading(false));
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full border-white/10 bg-[hsl(var(--card))] text-foreground sm:max-w-md" data-testid="sicurezza-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-display text-foreground"><ShieldCheck className="h-5 w-5 text-primary" /> Attività di sicurezza</SheetTitle>
          <SheetDescription className="text-xs text-muted-foreground">Accessi all'area amministratore e copie di sicurezza dei dati.</SheetDescription>
        </SheetHeader>
        <div className="mt-4 flex gap-1 rounded-xl border border-white/5 bg-black/20 p-1">
          <button onClick={() => setView("logs")} data-active={view === "logs"} className="tab-pill flex-1" data-testid="sicurezza-tab-logs">Accessi</button>
          <button onClick={() => setView("backup")} data-active={view === "backup"} className="tab-pill flex-1" data-testid="sicurezza-tab-backup">Backup</button>
        </div>
        {view === "backup" ? <div className="mt-4 overflow-y-auto pb-6 pr-1" style={{ maxHeight: "calc(100vh - 170px)" }}><BackupPanel onRestored={onRestored} /></div> : (
        <div className="mt-5 space-y-1.5 overflow-y-auto pb-6 pr-1" style={{ maxHeight: "calc(100vh - 130px)" }} data-testid="security-log-list">
          {loading ? <div className="flex justify-center py-8 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
            : logs.length === 0 ? <div className="py-8 text-center text-sm text-muted-foreground">Nessun evento registrato.</div>
            : logs.map((l, i) => (
              <div key={`${l.at}-${i}`} className="flex items-start gap-2.5 rounded-lg border border-white/10 bg-black/20 px-3 py-2" data-testid={`security-log-${i}`}>
                {l.success ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(0_72%_65%)]" />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold">{EVENTS[l.event] || l.event}</span>
                    <span className="shrink-0 font-mono-num text-[10px] text-muted-foreground">{new Date(l.at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "medium" })}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground">{REASONS[l.reason] || l.reason || (l.success ? "Riuscito" : "Fallito")}</div>
                  <div className="truncate text-[10px] text-muted-foreground/70">{l.ip} · {device(l.user_agent || "")}</div>
                </div>
              </div>
            ))}
        </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
